import { type ReactNode, useState, type ContextType } from 'react';
import { AppContext } from './AppContext';
import { Routes } from '../../Routes';
import type { SubmitResponse } from '../api/submit';
import { parseMsisdn } from '../util/msisdn';
import { getParameterByName } from '../util/util';
import { type ParsedClaims, getDefaultCountry } from './claims';
import { env } from '../util/env.ts';
import { mixpanelService } from '../services/mixpanel.ts';
import type { ErrorReason } from '../services/mixpanelEvents.ts';

const getInitialAppContext = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const hasAttemptedRedirect = urlParams.get('redirectAttempted') === 'true';

  const context: ContextType<typeof AppContext> = {
    apiAuth: getParameterByName('token') || '',
    apiSubscriptionId: env.VITE_SUBSCRIPTION_ID,
    currentRoute: Routes.LANDING,
    setSubmitResponse: () => void 0,
    setRoute: () => void 0,
    updateClaims: () => void 0,
    claims: null,
    hasAttemptedRedirect,
    mixpanel: mixpanelService,
  };

  return context;
};

export const AppContextProvider = ({
  children,
  overrides,
}: {
  children: ReactNode;
  overrides?: Partial<ContextType<typeof AppContext>>;
}) => {
  const [route, setCurrentRoute] = useState<Routes>(Routes.LANDING);
  const [errorReason, setErrorReason] = useState<ErrorReason | undefined>(undefined);
  const [claims, setClaims] = useState<ParsedClaims | null>(null);
  const [submitResponse, setSubmitResponse] = useState<SubmitResponse | undefined>(undefined);

  const setRoute = (nextRoute: Routes, reason?: ErrorReason) => {
    setErrorReason(reason);
    setCurrentRoute(nextRoute);
  };

  const contextValue = {
    ...getInitialAppContext(),
    currentRoute: route,
    errorReason,
    claims,
    submittedPhoneNumber:
      submitResponse ?
        parseMsisdn(getDefaultCountry(claims), submitResponse?.phoneNumber)
      : undefined,
    setRoute,
    setSubmitResponse: setSubmitResponse,
    updateClaims: setClaims,
    ...overrides,
  };

  return <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>;
};
